<script>
import { Storage } from "aws-amplify";
import { GET_USER_ATTRIBUTES } from "@/store/auth.module";
import { currencyFormat, awsDateToDate } from "@/services/Formatting.js";

const codeimpleImage = "/assets/media/logos/codeimple.png";

export default {
  props: {
    receiptDetails: {
      type: Object,
      required: true
    },
    recurringInfo: {
      type: Object,
      default() {
        return {};
      }
    },
    merchantId: {
      type: String,
      required: false,
      default: null
    },
    isRecurring: Boolean
  },
  data() {
    return {
      showLogo: false,
      logo: null,
      codeimpleImage: codeimpleImage
    };
  },
  computed: {
    receiptTitle() {
      if (this.isRecurring) return "Recurring Receipt";
      return "Receipt";
    },
    merchantLogo() {
      return this.logo;
    },
    transactionDate() {
      const date = this.receiptDetails.date
        ? new Date(this.receiptDetails.date)
        : new Date().toISOString().substring(0, 10);
      const dateString = date.toDateString();
      const timeString = date.toLocaleTimeString();
      return `${dateString} ${timeString}`;
    },
    total() {
      if (this.isRecurring) {
        return this.recurringAmount;
      }
      return this.amount;
    },
    amount() {
      return currencyFormat(this.receiptDetails.amount);
    },
    subtotal() {
      if (this.isRecurring) {
        return this.recurringSubtotal;
      }
      return currencyFormat(this.receiptDetails.originalAmount);
    },
    recurringSubtotal() {
      if (this.recurringInfo && this.recurringInfo.recurringOriginalAmount)
        return currencyFormat(this.recurringInfo.recurringOriginalAmount);
      return 0;
    },
    recurringAmount() {
      if (this.recurringInfo && this.recurringInfo.recurringAmount)
        return currencyFormat(this.recurringInfo.recurringAmount);
      return 0;
    },
    executionDate() {
      if (!this.recurringInfo || !this.recurringInfo.executionDate)
        return new Date();
      const execDate = awsDateToDate(this.recurringInfo.executionDate);
      return execDate.toDateString();
    },
    period() {
      if (!this.recurringInfo || !this.recurringInfo.period) return "";
      const period = this.recurringInfo.period;
      if (period === -1) return "Continuous";
      return period;
    },
    discounts() {
      return this.receiptDetails.discounts.map(d => {
        return { ...d, Amount: currencyFormat(d.Amount) };
      });
    }
  },
  methods: {
    async getMerchantId() {
      var result = null;
      await this.$store.dispatch(GET_USER_ATTRIBUTES).then(attributes => {
        result = attributes["family_name"];
      });
      if (!result) throw new Error("No merchantId exists for this user.");
      return result;
    },
    async retrieveLogo() {
      const vm = this;
      Storage.get(
        `merchant/logo/${this.merchantId ?? (await this.getMerchantId())}`
      )
        .then(result => {
          vm.logo = result;
          vm.showLogo = true;
        })
        .catch(err => {
          this.$logger.error(err);
          vm.logo = null;
        });
    }
  },
  async mounted() {
    this.$nextTick().then(async () => {
      await this.retrieveLogo();
    });
  }
};
</script>

<template>
  <div class="d-flex justify-content-center">
    <b-card class="receipt">
      <template>
        <div class="img">
          <img :src="merchantLogo" />
        </div>
        <div class="heading">
          <h1 id="payment-receipt-title">{{ receiptTitle }}</h1>
        </div>
      </template>

      <b-container>
        <b-row class="r-col-main">
          <b-col cols="3" class="r-col r-col-1"></b-col>
          <b-col class="r-col r-col-2">
            <div class="r-col-data">
              <h3>Customer</h3>
              <P>{{ receiptDetails.name }}</P>
            </div>
            <!-- Customer-->
            <div class="r-col-data">
              <h3>Receipt Number</h3>
              <p>{{ receiptDetails.receiptNumber }}</p>
            </div>
            <!-- Reciept Number-->
            <div class="r-col-data">
              <h3>Transaction Date</h3>
              <p>{{ transactionDate }}</p>
            </div>
            <!-- Transaction Date-->
          </b-col>
          <b-col cols="1" class="r-col r-col-3"></b-col>
          <b-col class="r-col r-col-4">
            <div class="r-col-data">
              <h3>Payment Information</h3>
              <p>Authorization Number: {{ receiptDetails.authNumber }}</p>
            </div>
            <!--Payment Information-->
            <div class="r-col-data">
              <h3>Payment Summary</h3>
              <div v-if="!isRecurring">
                <template v-for="(discount, index) in discounts">
                  <div :key="index">
                    {{ discount.Name }}: -
                    <p>
                      ${{ discount.Amount }} {{ receiptDetails.currencyText }}
                    </p>
                  </div>
                </template>
                <p>
                  Total:
                  <span class="r-total"
                    >${{ this.total }} {{ receiptDetails.currencyText }}</span
                  >
                </p>
              </div>
              <div v-else>
                <p>First Payment: {{ this.executionDate }}</p>
                <p>Frequency: {{ recurringInfo.frequency }}</p>
                <p>
                  Initial Payment:
                  <span class="r-total"
                    >${{ amount }} {{ receiptDetails.currencyText }}</span
                  >
                </p>
                <p>
                  Subsequent Payment:
                  <span class="r-total"
                    >${{ recurringAmount }}
                    {{ receiptDetails.currencyText }}</span
                  >
                </p>
                <p>{{ recurringInfo.paymentDescription }}</p>
              </div>
            </div>
            <!--Payment Summary-->
            <div class="r-col-data">
              <h3>Card Information</h3>
              <p>{{ receiptDetails.paddedCardNumber }}</p>
            </div>
            <!--Card Information-->
          </b-col>
          <b-col cols="3" class="r-col r-col-5"></b-col>
        </b-row>
      </b-container>

      <template v-slot:footer>
        <b-row>
          <b-col class="d-flex align-items-end">
            <p class="font-italic small mb-0 text-black-50 r-logo">
              powered by PAYNOWPRO
            </p>
          </b-col>
        </b-row>
      </template>
    </b-card>
  </div>
</template>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap");

* {
  margin: 0;
  padding: 0;
  font-family: "Open Sans", sans-serif;
  box-sizing: border-box;
}
.r-logo {
  font-size: 1.5rem;
  margin-left: 0.8rem;
}
.r-col-4,
.r-col-2 {
  margin-bottom: 4rem;
}

.r-col-data {
  margin: 2rem 0;
}

.r-col-data h3 {
  color: #222325;
  font-family: "Open Sans";
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 0;
  margin-bottom: 10px;
  line-height: 20px;
}
.r-col-data p {
  color: #222325;
  font-family: "Open Sans";
  font-size: 14px;
  letter-spacing: 0;
  line-height: 20px;
  margin-bottom: 5px;
}

.receipt {
  border-radius: 6px 6px 0 0;
  background-color: #ffffff;
  margin: 0;
  margin-top: 2rem;
  height: 100%;
  width: 100%;
  display: grid;
}
.heading {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: 2rem 0 3rem 0;
}

.img img {
  width: 50%;
  height: 50%;
}
.heading h1 {
  font-family: "Open Sans";
  font-size: 40px;
  font-weight: 300;
  letter-spacing: 0;
  line-height: 40px;
  text-align: center;
}

.img {
  margin-top: 5rem;
  display: flex;
  justify-content: center;
  align-items: center;
}
.r-total {
  font-weight: 600 !important;
}

@media screen and (max-width: 550px) {
  .r-col-1,
  .r-col-3,
  .r-col-5 {
    display: none;
  }
  .r-col-main {
    flex-direction: column;
  }

  .r-col-4,
  .r-col-2 {
    margin-bottom: 1rem;
    padding-left: 1rem;
  }
}
</style>
